import { createNestablePublicClientApplication, PublicClientApplication  } from "@azure/msal-browser";
import { CalendarEventResponse, DateTimeTimeZone, OutlookCalendarEvent, PamsCalendarResponse, PamsCalendarResponseDetails, PamsCalendarTypeEnum } from "./calendar.model";
import { requestHeaderWithAccessToken, serverUrl, showStatusMessage } from "../auth/auth.service";

let listOfConfirmedIds: number[] = [];
let pamsCalendarResponse: PamsCalendarResponse;
let lengthOfPamsCalendar: number = 0;

// Batch configuration
const BATCH_SIZE = 20; // Microsoft Graph supports max 20 requests per batch
const BATCH_DELAY = 1000; // 1 second delay between batches

function generateEventIdPattaren(event:PamsCalendarResponseDetails):string{
  return `${PamsCalendarTypeEnum[event.type]}-${event.id}`;
}

function addTimeZoneToDate(date: Date, allDayEvent: boolean):string {
  // Apply your timezone logic
  if(!allDayEvent){
    const timezoneOffset = date.getTimezoneOffset();
    const tzDifference = (timezoneOffset * -1);
    const offsetTime = new Date(date.getTime() + tzDifference * 60 * 1000);
    // Convert to Graph format (remove 'Z')
    const localString = offsetTime.toLocaleString();
    const localStringWithoutZ = localString.substring(0, localString.length - 1); // Remove 'Z'
    return localString;
  }
  else{
    const localString = date.toLocaleString();
    const localStringWithoutZ = localString.substring(0, localString.length - 1); // Remove 'Z'
    return localString;
  }
}

function cloneDateWithoutTimezone(date:Date): string{
  const year = new Date(date).getFullYear();
  const month = new Date(date).getMonth();
  const day = new Date(date).getDate();
  const hour = new Date(date).getHours();
  const min = new Date(date).getMinutes();
  const sec = new Date(date).getSeconds();
  date.setHours(hour);
  date.setMinutes(min);
  date.setMilliseconds(sec);
  let newDate = date.toLocaleString();
  newDate.substring(0, newDate.length - 1); // Remove 'Z'
  return newDate;
}

function subtractTimeZoneToDate(date: Date) {
    var offsetTime: Date = date;
    var timeZoneFromDB = 0; //time zone value from database
    var timezoneOffset = date.getTimezoneOffset();
    var tzDifference = (timeZoneFromDB * 60) + (timezoneOffset * -1);
    offsetTime = new Date(date.getTime() - tzDifference * 60 * 1000);
    return offsetTime;
}

/* method access token #2 */
let MSAL_token: string;
const msalConfig = {
  auth: {
    clientId: 'd9801208-fe70-4b23-bc46-824b01f7ea72',
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: window.location.origin
  }
};
const pca = new PublicClientApplication(msalConfig);

export async function getAccessTokenStandard() {
  try {
    await pca.initialize();
    const accounts = pca.getAllAccounts();
    const tokenRequest = {
      scopes: ['https://graph.microsoft.com/Calendars.ReadWrite', 'https://graph.microsoft.com/User.Read']
    };
    if (accounts.length > 0) {
      const silentRequest = { ...tokenRequest, account: accounts[0] };
      try {
        const result = await pca.acquireTokenSilent(silentRequest);
        return result.accessToken;
      } catch (error) {
        console.error('Silent authentication failed, trying popup');
        showStatusMessage(`${error instanceof Error ? error.message : 'Silent authentication failed'}`, "error");
      }
    }
    const result = await pca.acquireTokenPopup(tokenRequest);
    MSAL_token = result.accessToken;
    return result.accessToken;
  }
   catch (error) {
    console.error('Authentication failed:', error);
    showStatusMessage(`${error instanceof Error ? error.message : 'Microsoft authentication failed:'}`, "error");
    throw error;
  }
}

// Helper function to delay execution
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Batch API Types
interface BatchRequest {
  id: string;
  method: string;
  url: string;
  body?: any;
  headers?: any;
}

interface BatchResponse {
  id: string;
  status: number;
  body: any;
}

// Create batch request for event creation/update
function createEventBatchRequest(event: OutlookCalendarEvent, index: number): BatchRequest {
  const isUpdate = !!event.id;
  return {
    id: index.toString(),
    method: isUpdate ? "PATCH" : "POST",
    url: isUpdate ? `/me/events/${event.id}` : "/me/events",
    body: event,
    headers: {
      "Content-Type": "application/json"
    }
  };
}

// Create batch request for event deletion
function createDeleteBatchRequest(eventId: string, index: number): BatchRequest {
  return {
    id: index.toString(),
    method: "DELETE",
    url: `/me/events/${eventId}`
  };
}

// Execute batch request with retry logic
async function executeBatchRequest(
  requests: BatchRequest[], 
  accessToken: string,
  retryCount = 0
): Promise<BatchResponse[]> {
  const maxRetries = 3;
  const retryDelay = 1000 * Math.pow(2, retryCount); // Exponential backoff
  
  try {
    const response = await fetch('https://graph.microsoft.com/v1.0/$batch', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ requests })
    });

    if (response.status === 429) {
      if (retryCount < maxRetries) {
        const retryAfter = response.headers.get('Retry-After') || retryDelay / 1000;
        console.log(`Rate limited. Retrying batch after ${retryAfter} seconds...`);
        //showStatusMessage(`Rate limited. Retrying in ${retryAfter} seconds...`, "info");
        await delay(Number(retryAfter) * 1000);
        return executeBatchRequest(requests, accessToken, retryCount + 1);
      }
      throw new Error('Max retries exceeded for batch request');
    }

    if (!response.ok) {
      throw new Error(`Batch request failed: ${response.status}`);
    }

    const data = await response.json();
    return data.responses;
  } catch (error) {
    console.error('Batch request error:', error);
    throw error;
  }
}

// Process batch responses and handle confirmations
function processBatchResponses(
  responses: BatchResponse[], 
  eventMapping: Map<string, number>
): { successful: number; failed: number } {
  let successful = 0;
  let failed = 0;

  responses.forEach(response => {
    const rowId = eventMapping.get(response.id);
    if (response.status >= 200 && response.status < 300) {
      successful++;
      if (rowId) {
        checkConfirmation(rowId);
      }
    } else {
      failed++;
      console.error(`Request ${response.id} failed with status ${response.status}:`, response.body);
    }
  });

  return { successful, failed };
}

// Build event object for creation/update
function buildEventObject(
  event: PamsCalendarResponseDetails, 
  existingEvent?: OutlookCalendarEvent
): OutlookCalendarEvent {
  let isAllDay = false;
  let startDate = new Date(event.startDate);
  let endDate = new Date(event.endDate);
  let eventTitle = traceType(event.type) + ": " + event.title;
  
  if (event.type === PamsCalendarTypeEnum.BiddueDate || 
      event.type === PamsCalendarTypeEnum.Deliverable) {
    isAllDay = true;
    startDate = new Date(new Date(event.startDate).setHours(0, 0, 0, 0));
    endDate = new Date(new Date(event.endDate).setHours(24, 0, 0, 0));
    eventTitle += (event.ClientName)? " - " + event.ClientName : "";
  }
  
  
  if (existingEvent?.id) {
    return {
      ...existingEvent,
      "@odata.etag": "",
      subject: eventTitle,
      isAllDay: isAllDay,
      start: {
        dateTime: addTimeZoneToDate(startDate, isAllDay),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      end: {
        dateTime: addTimeZoneToDate(endDate, isAllDay),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      body: {
        contentType: "text",
        content: eventTitle
      },
      transactionId: generateEventIdPattaren(event)
    };
  } else {
    return {
      subject: eventTitle,
      isAllDay: isAllDay,
      start: {
        dateTime: addTimeZoneToDate(startDate, isAllDay),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      end: {
        dateTime: addTimeZoneToDate(endDate, isAllDay),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      body: {
        contentType: 'text',
        content: eventTitle
      },
      transactionId: generateEventIdPattaren(event)
    };
  }
}

// OPTIMIZED: Main function to match and sync calendar events
export async function matchingPamsCalendarWithOutlookCalendar(
  pamsCalendar: PamsCalendarResponse, 
  outlookCalendar: OutlookCalendarEvent[]
) {
  pamsCalendarResponse = pamsCalendar;
  lengthOfPamsCalendar = pamsCalendar.Active.length + pamsCalendar.Removed.length;
  listOfConfirmedIds = [];

  if (lengthOfPamsCalendar === 0) {
    //showStatusMessage("No calendar events to sync", "info");
    return;
  }

  try {
    // Get access token once for all operations
    //showStatusMessage("Authenticating with Microsoft...", "info");
    const accessToken = await getAccessTokenStandard();
    
    // Prepare all event operations
    const createUpdateRequests: BatchRequest[] = [];
    const deleteRequests: BatchRequest[] = [];
    const eventMapping = new Map<string, number>(); // Maps request ID to rowId
    
    // Process active events
    if (pamsCalendar.Active?.length) {
      pamsCalendar.Active.forEach((event, index) => {
        const eventIdPattern = generateEventIdPattaren(event);
        const existingEvent = outlookCalendar.find(x => x.transactionId === eventIdPattern);
        
        const eventObject = buildEventObject(event, existingEvent);
        const request = createEventBatchRequest(eventObject, index);
        createUpdateRequests.push(request);
        eventMapping.set(index.toString(), event.rowId);
      });
    }
    
    // Process removed events
    if (pamsCalendar.Removed?.length) {
      pamsCalendar.Removed.forEach((event, index) => {
        const eventIdPattern = generateEventIdPattaren(event);
        const existingEvent = outlookCalendar.find(x => x.transactionId === eventIdPattern);
        
        if (existingEvent?.id) {
          const request = createDeleteBatchRequest(existingEvent.id, index + 1000); // Offset index to avoid conflicts
          deleteRequests.push(request);
          eventMapping.set((index + 1000).toString(), event.rowId);
        }
      });
    }
    
    // Process requests in batches
    const allRequests = [...createUpdateRequests, ...deleteRequests];
    const batches: BatchRequest[][] = [];
    
    for (let i = 0; i < allRequests.length; i += BATCH_SIZE) {
      batches.push(allRequests.slice(i, i + BATCH_SIZE));
    }
    
    let totalSuccessful = 0;
    let totalFailed = 0;
    
    //showStatusMessage(`Processing ${allRequests.length} events in ${batches.length} batches...`, "info");
    
    // Execute batches with delay between them
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];
      //showStatusMessage(`Processing batch ${i + 1}/${batches.length}...`, "info");
      
      try {
        const responses = await executeBatchRequest(batch, accessToken);
        const { successful, failed } = processBatchResponses(responses, eventMapping);
        
        totalSuccessful += successful;
        totalFailed += failed;
        
        // Add delay between batches to avoid throttling
        if (i < batches.length - 1) {
          await delay(BATCH_DELAY);
        }
      } catch (error) {
        console.error(`Batch ${i + 1} failed:`, error);
        totalFailed += batch.length;
      }
    }
    
    // Show final results
    const message = `Calendar sync completed: ${totalSuccessful} successful, ${totalFailed} failed`;
    showStatusMessage(message, totalFailed > 0 ? "error" : "success");
    
  } catch (error) {
    console.error('Calendar sync failed:', error);
    showStatusMessage(`Calendar sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`, "error");
  }
}

// Helper functions remain the same
export function checkConfirmation(rowId: number) {
  if (rowId) {
    if (!listOfConfirmedIds || !listOfConfirmedIds.length) {
      listOfConfirmedIds = [];
    }
    listOfConfirmedIds.push(rowId);
  }
  if (lengthOfPamsCalendar == listOfConfirmedIds.length) {
    try {
      confirmSavedPamsCalendar(listOfConfirmedIds).then(
        (value) => {
          // Success
        })
        .catch(
          (error) => {
            // Error
          });
    } catch (error) {
      // Error
    }
  }
}

export async function confirmSavedPamsCalendar(Ids: number[]): Promise<number> {
  try {
    const response = await fetch(serverUrl + `Tasks/Task/confirmoutlookcalendarupdate`,
      {
        method: "PUT",
        headers: requestHeaderWithAccessToken(),
        body: JSON.stringify(Ids)
      }
    );
    if (response.ok) return response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}

export async function getPamsCalendar(): Promise<PamsCalendarResponse> {
  try {
    const response = await fetch(serverUrl + `Tasks/Task/getoutlookcalendar`,
      {
        method: "GET",
        headers: requestHeaderWithAccessToken(),
      }
    );
    if (response.ok) return response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}

export async function checkIftherePamsCalendar(): Promise<number> {
  try {
    const response = await fetch(serverUrl + `Tasks/Task/checkoutlookcalendar`,
      {
        method: "GET",
        headers: requestHeaderWithAccessToken(),
      }
    );
    if (response.ok) return response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
}

export async function getCalendarEvents(startDate: Date, endDate: Date, token: string): Promise<OutlookCalendarEvent[]> {
  const start = (startDate) ? startDate.toISOString() : "";
  const end = (endDate) ? new Date(endDate.setFullYear(endDate.getFullYear() + 1)).toISOString() : "";
  
  if (start && end) {
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/me/calendar/calendarView?startDateTime=${start}&endDateTime=${end}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    const data = await response.json();
    return data.value;
  }
}

// Individual operation functions (preserved for compatibility)
// These can still be called if needed by other parts of the codebase
export async function handleSaveUpdateCalendarEvent(eventData: OutlookCalendarEvent, rowId: number) {
  try {
    const token = await getAccessTokenStandard();
    const isUpdate = !!eventData.id;
    const URL = isUpdate 
      ? `https://graph.microsoft.com/v1.0/me/events/${eventData.id}`
      : "https://graph.microsoft.com/v1.0/me/events";
    
    const response = await fetch(URL, {
      method: isUpdate ? "PATCH" : "POST",
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(eventData)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const result = await response.json();
    checkConfirmation(rowId);
    return result;
    
  } catch (error) {
    console.error('Error when event created/updated:', error);
    showStatusMessage(`${error instanceof Error ? error.message : 'Updating calendar error'}`, "error");
    throw error;
  }
}

async function handleSaveUpdateAppointments(event: PamsCalendarResponseDetails, OutlookAppointment?: OutlookCalendarEvent) {
  const eventObject = buildEventObject(event, OutlookAppointment);
  return handleSaveUpdateCalendarEvent(eventObject, event.rowId);
}

async function handleDeletingAppointments(event: OutlookCalendarEvent, rowId: number) {
  try {
    const token = await getAccessTokenStandard();
    
    if (event?.id) {
      const response = await fetch(`https://graph.microsoft.com/v1.0/me/events/${event.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete event: ${response.status} - ${errorText}`);
      }
      
      checkConfirmation(rowId);
    }
  } catch (error) {
    console.error('Error when event deleted:', error);
    showStatusMessage(`${error instanceof Error ? error.message : 'Microsoft outlook token error'}`, "error");
    throw error;
  }
}

function traceType(id) {
  if (id === PamsCalendarTypeEnum.Task) {
    return "Task";
  }
  else if (id === PamsCalendarTypeEnum.Visit) {
    return "Visit";
  }
  else if (id === PamsCalendarTypeEnum.Meeting) {
    return "Meeting";
  }
  else if (id === PamsCalendarTypeEnum.Email) {
    return "Mail"
  }
  else if (id === PamsCalendarTypeEnum.Call) {
    return "Call";
  }
  else if (id === PamsCalendarTypeEnum.BiddueDate) {
    return "BDD";
  }
  else if (id === PamsCalendarTypeEnum.Receivable) {
    return "Goods receipt dates";
  }
  else if (id === PamsCalendarTypeEnum.Deliverable) {
    return "Delivery dates";
  }
  return "";
}
